/**
 * Stable Device Identifier
 * 
 * Generates and persists a stable device ID that survives sessions.
 * - PWA/Browser: stored in IndexedDB (fallback to localStorage)
 * - Native: stored in secure storage via Capacitor
 */

import { Capacitor } from '@capacitor/core';

const DEVICE_ID_KEY = 'lupuslogix_device_id';
const DB_NAME = 'lupuslogix_device_store';
const STORE_NAME = 'device_info';

/**
 * Synchronous read for device id.
 * Used for instant UI hydration (e.g., push enabled state) without awaiting IndexedDB.
 */
export function getDeviceIdSync(): string | null {
  try {
    return localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    return null;
  }
}

// Generate a new UUID
function generateDeviceId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// IndexedDB helpers
async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

async function getFromIndexedDB(key: string): Promise<string | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || null);
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.warn('[DeviceId] IndexedDB read failed:', error);
    return null;
  }
}

async function setInIndexedDB(key: string, value: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(value, key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.warn('[DeviceId] IndexedDB write failed:', error);
    throw error;
  }
}

// Native storage helpers - use localStorage as cross-platform fallback
// (Capacitor Preferences would require additional package installation)
async function getFromNativeStorage(): Promise<string | null> {
  try {
    // Use localStorage as it works on all platforms including Capacitor webview
    return localStorage.getItem(DEVICE_ID_KEY);
  } catch (error) {
    console.warn('[DeviceId] Native storage read failed:', error);
    return null;
  }
}

async function setInNativeStorage(deviceId: string): Promise<void> {
  try {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  } catch (error) {
    console.warn('[DeviceId] Native storage write failed:', error);
  }
}

/**
 * Get or create a stable device identifier
 * 
 * CRITICAL: This ID must persist across PWA reinstalls and cache clears.
 * Priority: localStorage (most persistent) → IndexedDB (for SW access) → generate new
 * Always sync to both stores to ensure service worker can access it.
 */
export async function getDeviceId(): Promise<string> {
  const isNative = Capacitor.isNativePlatform();
  
  try {
    let deviceId: string | null = null;
    
    if (isNative) {
      // Native platform: use localStorage (works in Capacitor webview)
      deviceId = await getFromNativeStorage();
      
      if (!deviceId) {
        deviceId = generateDeviceId();
        await setInNativeStorage(deviceId);
        console.log('[DeviceId] Generated new native device ID:', deviceId.substring(0, 8) + '...');
      }
    } else {
      // Web platform: localStorage is PRIMARY (survives PWA reinstalls better)
      // IndexedDB is SECONDARY (for service worker access)
      
      // Step 1: Check localStorage FIRST (most persistent)
      deviceId = localStorage.getItem(DEVICE_ID_KEY);
      
      if (deviceId) {
        // Ensure IndexedDB is synced for service worker access
        try {
          const indexedDbId = await getFromIndexedDB(DEVICE_ID_KEY);
          if (indexedDbId !== deviceId) {
            await setInIndexedDB(DEVICE_ID_KEY, deviceId);
            console.log('[DeviceId] Synced localStorage ID to IndexedDB');
          }
        } catch {
          // IndexedDB sync failed, but we have localStorage - continue
          console.warn('[DeviceId] IndexedDB sync failed, using localStorage only');
        }
      } else {
        // Step 2: Check IndexedDB (might have survived if localStorage was cleared)
        try {
          deviceId = await getFromIndexedDB(DEVICE_ID_KEY);
          if (deviceId) {
            // Restore to localStorage
            localStorage.setItem(DEVICE_ID_KEY, deviceId);
            console.log('[DeviceId] Restored device ID from IndexedDB to localStorage');
          }
        } catch {
          // IndexedDB failed, continue to generate new
        }
      }
      
      // Step 3: Generate new ID only if neither store has one
      if (!deviceId) {
        deviceId = generateDeviceId();
        localStorage.setItem(DEVICE_ID_KEY, deviceId);
        try {
          await setInIndexedDB(DEVICE_ID_KEY, deviceId);
        } catch {
          console.warn('[DeviceId] Could not sync new ID to IndexedDB');
        }
        console.log('[DeviceId] Generated new web device ID:', deviceId.substring(0, 8) + '...');
      }
    }
    
    return deviceId;
  } catch (error) {
    console.error('[DeviceId] Error getting device ID:', error);
    
    // Ultimate fallback: use localStorage directly
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = generateDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  }
}

/**
 * Get device name for display purposes
 */
export function getDeviceName(): string {
  const userAgent = navigator.userAgent;
  
  // Check for iOS
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    if (/iPad/.test(userAgent)) return 'iPad';
    return 'iPhone';
  }
  
  // Check for Android
  if (/Android/.test(userAgent)) {
    const match = userAgent.match(/\(Linux; Android [^;]+; ([^)]+)\)/);
    if (match && match[1]) {
      return match[1].split(' Build')[0].trim();
    }
    return 'Android Device';
  }
  
  // Check for Mac
  if (/Macintosh/.test(userAgent)) {
    return 'Mac';
  }
  
  // Check for Windows
  if (/Windows/.test(userAgent)) {
    return 'Windows PC';
  }
  
  // Check for Linux
  if (/Linux/.test(userAgent)) {
    return 'Linux PC';
  }
  
  return 'Unknown Device';
}

/**
 * Get OS version
 */
export function getOSVersion(): string {
  const userAgent = navigator.userAgent;
  
  // iOS
  const iosMatch = userAgent.match(/OS (\d+[._]\d+([._]\d+)?)/);
  if (iosMatch) {
    return 'iOS ' + iosMatch[1].replace(/_/g, '.');
  }
  
  // Android
  const androidMatch = userAgent.match(/Android (\d+(\.\d+)?)/);
  if (androidMatch) {
    return 'Android ' + androidMatch[1];
  }
  
  // Windows
  const windowsMatch = userAgent.match(/Windows NT (\d+\.\d+)/);
  if (windowsMatch) {
    const versions: Record<string, string> = {
      '10.0': 'Windows 10/11',
      '6.3': 'Windows 8.1',
      '6.2': 'Windows 8',
      '6.1': 'Windows 7'
    };
    return versions[windowsMatch[1]] || 'Windows';
  }
  
  // macOS
  const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+([._]\d+)?)/);
  if (macMatch) {
    return 'macOS ' + macMatch[1].replace(/_/g, '.');
  }
  
  return 'Unknown OS';
}
